import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    HostListener,
    Inject,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import { ApplicationFacade, BaseRoute, ShapeType } from '@ridango/components';
import { IonAccordionGroup } from '@ionic/angular';
import { AppStartupService, DepartureArrivalTime } from '@ridango/core';
import Env from '@ridango/core/env';
import { DatePipe } from '@angular/common';
import { RouteFilterUtil } from './utils/route-filter.util';
import { TimeFilterUtil } from './utils/time-filter.util';
import {
    ROUTE_ICON_SIZES,
    ROUTE_ICON_TEXT_SIZES,
    RouteIconSize,
    RouteIconTextSize
} from '../route-short-name/route-short-name.component';

@Component({
    selector: 'rwidget-depart-filter',
    templateUrl: './depart-filter.component.html',
    styleUrls: ['./depart-filter.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DepartFilterComponent implements OnInit, OnChanges, AfterViewInit {
    @Input() routes: BaseRoute[] = [];
    @Input() filterDepartTime: DepartureArrivalTime;
    @Input() spaceWhenNotExpanded = false;
    @Output() filterRouteShortNamesChange: EventEmitter<string[]> = new EventEmitter<string[]>();
    @Output() filterDepartTimeChange: EventEmitter<DepartureArrivalTime> = new EventEmitter<DepartureArrivalTime>();

    @ViewChild('filterAccordionGroup', { static: true }) accordionGroup: IonAccordionGroup;
    public selectedRoutes: BaseRoute[] = [];
    private readonly timezone: string = this.appStartupService.getTimezone();
    isTimeFilterSet = false;
    timeLabel = '';

    assetsPath$ = this.applicationFacade.assetsPath$;
    ShapeType = ShapeType;
    minDateForFilter: string;
    maxDateForFilter: string;
    initialWindowWidth: number = window.innerWidth;
    isMobileView: boolean = window.innerWidth < 768;
    routeIconSize: RouteIconSize = 'lr'; // Default size
    routeIconTextSize: RouteIconTextSize = 'sm';
    routeIconDisabledColor = '#838383';

    @HostListener('window:resize')
    onResize() {
        if (this.initialWindowWidth !== window.innerWidth) {
            this.initialWindowWidth = window.innerWidth;
            this.isMobileView = window.innerWidth < 768;
        }
    }

    constructor(
        private readonly applicationFacade: ApplicationFacade,
        private readonly datePipe: DatePipe,
        @Inject('ENV') public env: Env,
        private readonly appStartupService: AppStartupService
    ) {
        const departFilterConfig = this.env.EXPLORE?.STOP_VIEW?.DEPART_FILTER;

        const routeIconSize = departFilterConfig?.ROUTE_ICON_SIZE;
        this.routeIconSize = this.isRouteIconSize(routeIconSize) ? routeIconSize : 'lr';

        const routeIconTextSize = departFilterConfig?.ROUTE_ICON_TEXT_SIZE;
        this.routeIconTextSize = this.isRouteIconTextSize(routeIconTextSize) ? routeIconTextSize : 'sm';

        const routeIconDisabledColor = departFilterConfig?.ROUTE_ICON_DISABLED_COLOR;
        this.routeIconDisabledColor = routeIconDisabledColor?.trim() || '#838383';
    }

    ngAfterViewInit() {
        if (this.env.EXPLORE?.STOP_VIEW?.NOT_EXPANDABLE_FILTER) {
            this.accordionGroup.value = 'first';
        } else {
            this.accordionGroup.value = this.spaceWhenNotExpanded ? 'second' : undefined;
        }
    }

    ngOnInit() {
        this.minDateForFilter = TimeFilterUtil.getMinDateForFilter(this.timezone);
        this.maxDateForFilter = TimeFilterUtil.getMaxDateForFilter(this.timezone);
        this.updateTimeFilterState();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.routes) {
            this.selectedRoutes = this.selectedRoutes.filter((selectedRoute) =>
                this.routes.some((route) => route.id === selectedRoute.id)
            );
            this.emitFilterRouteShortNamesChangeChanges();
        }
        if (
            changes?.filterDepartTime &&
            changes.filterDepartTime.currentValue !== changes.filterDepartTime.previousValue &&
            changes.filterDepartTime.previousValue !== undefined
        ) {
            this.updateTimeFilterState();
        }
    }

    onFilterDepartTimeChange(newTime: DepartureArrivalTime): void {
        this.filterDepartTime = newTime;
        this.updateTimeFilterState();
        this.filterDepartTimeChange.emit(newTime);
    }

    toggleRoute(route: BaseRoute) {
        this.selectedRoutes = RouteFilterUtil.toggleRoute(route, this.selectedRoutes);
        this.emitFilterRouteShortNamesChangeChanges();
    }

    isRouteSelected(route: BaseRoute): boolean {
        return RouteFilterUtil.isRouteSelected(route, this.selectedRoutes);
    }

    toggleFilterOpen() {
        if (this.accordionGroup.value === 'first') {
            this.accordionGroup.value = this.spaceWhenNotExpanded ? 'second' : undefined;
        } else {
            this.accordionGroup.value = 'first';
        }
    }

    get isFilterOpen(): boolean {
        return this.accordionGroup?.value === 'first';
    }

    get isNotExpandableFilter(): boolean {
        return !!this.env.EXPLORE?.STOP_VIEW?.NOT_EXPANDABLE_FILTER;
    }

    get filterButtonClasses(): string {
        return RouteFilterUtil.isFilterActive(this.selectedRoutes, this.routes)
            ? 'var-app-light-dark:rounded-xl var-app-light:bg-secondary orb-app-light-dark:rounded-xl orb-app-light:bg-secondary orb-app-light:text-white dark:bg-contrast-yellow dark:text-contrast-black'
            : '';
    }

    get filterIconClasses(): string {
        return RouteFilterUtil.isFilterActive(this.selectedRoutes, this.routes)
            ? 'dark:text-contrast-black'
            : 'dark:text-contrast-yellow';
    }

    get filterTextClasses(): string {
        return RouteFilterUtil.isFilterActive(this.selectedRoutes, this.routes) ? 'dark:text-contrast-black' : '';
    }

    private emitFilterRouteShortNamesChangeChanges() {
        const shortNames = RouteFilterUtil.getFilteredShortNames(this.selectedRoutes, this.routes);
        this.filterRouteShortNamesChange.emit(shortNames);
    }

    private updateTimeFilterState(): void {
        this.isTimeFilterSet = TimeFilterUtil.isTimeFilterActive(this.filterDepartTime, this.timezone);
        this.timeLabel = this.isTimeFilterSet
            ? TimeFilterUtil.formatTimeLabel(
                  this.filterDepartTime,
                  this.env.PRIMARY_DATE_TIME_FORMAT || this.env.PRIMARY_DATE_FORMAT_NGPIPE,
                  this.datePipe
              )
            : '';
    }

    private isRouteIconSize(value: unknown): value is RouteIconSize {
        return typeof value === 'string' && ROUTE_ICON_SIZES.includes(value as RouteIconSize);
    }

    private isRouteIconTextSize(value: unknown): value is RouteIconTextSize {
        return typeof value === 'string' && ROUTE_ICON_TEXT_SIZES.includes(value as RouteIconTextSize);
    }
}
